/**
 * VueCalendaire.js — Vues Jour / Semaine / Mois
 * Refonte complète — Design System SEIZO
 *
 * Charte : Sofia Pro / Nunito | #002335 | #00B0FF | #F4F7F9
 * - Header unifié avec toggle Kanban/Calendrier
 * - Barre navigation : mode + < Aujourd'hui >
 * - Grilles Jour/Semaine/Mois natives
 * - Création tâche au clic sur un créneau (date pré-remplie)
 * - Chips colorées par statut
 * - Drag & drop en vue semaine/mois pour changer date_estimee
 * - Drag & drop depuis boîte de réception vers calendrier
 * - Numéros de semaine ISO dans vue mois
 * - Section "Sans date" en bas
 * FIXES:
 *   - semaineCorps: 40px spacer + 7 colonnes (alignement avec header)
 *   - drag-drop depuis boîte de réception (application/reception-tache)
 *   - numéros de semaine ISO dans vue mois
 *   - persistance calViewMode + calCurrentDate dans localStorage
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { observer } from 'mobx-react';
import { useStores } from '../../../stores/Context';
import styles from './VueCalendaire.module.scss';
import axios from 'axios';

// ── Helpers ───────────────────────────────────────────────────────────────────

const JOURS_COURT = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];
const MOIS_LONG   = ['Janvier','Février','Mars','Avril','Mai','Juin',
                     'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const MOIS_COURT  = ['Jan.','Fév.','Mars','Avr.','Mai','Juin',
                     'Juil.','Août','Sept.','Oct.','Nov.','Déc.'];

/**
 * Convertit une date en "YYYY-MM-DD" en utilisant l'heure locale
 * (évite le décalage UTC+2 → date -1 jour)
 */
const toYMD = (d) => {
    const dd = new Date(d);
    return `${dd.getFullYear()}-${String(dd.getMonth()+1).padStart(2,'0')}-${String(dd.getDate()).padStart(2,'0')}`;
};

/**
 * Normalise une date_estimee ISO (ex: "2026-05-28T00:00:00Z") en YYYY-MM-DD local
 * sans décalage de fuseau
 */
const normalizeDate = (dateStr) => {
    if (!dateStr) return null;
    // Si c'est déjà YYYY-MM-DD (10 chars), on retourne tel quel
    if (typeof dateStr === 'string' && dateStr.length === 10) return dateStr;
    // Sinon on prend les 10 premiers chars de la représentation ISO
    const d = new Date(dateStr);
    return toYMD(d);
};

const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r; };

const startOfWeek = (d) => {
    const r = new Date(d);
    const day = r.getDay();
    r.setDate(r.getDate() + (day === 0 ? -6 : 1 - day));
    r.setHours(0, 0, 0, 0);
    return r;
};

const isToday = (d) => toYMD(d) === toYMD(new Date());

// Numéro de semaine ISO 8601
const getISOWeek = (d) => {
    const date = new Date(d);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() + 3 - (date.getDay() + 6) % 7);
    const week1 = new Date(date.getFullYear(), 0, 4);
    return 1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + (week1.getDay() + 6) % 7) / 7);
};

const getPeriodeLabel = (mode, date) => {
    if (mode === 'jour') {
        return date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
    if (mode === 'semaine') {
        const lundi = startOfWeek(date);
        const dim   = addDays(lundi, 6);
        return `${lundi.getDate()} ${MOIS_COURT[lundi.getMonth()]} — ${dim.getDate()} ${MOIS_COURT[dim.getMonth()]} ${dim.getFullYear()}`;
    }
    return `${MOIS_LONG[date.getMonth()]} ${date.getFullYear()}`;
};

// ── Icônes SVG ────────────────────────────────────────────────────────────────

const IcoLeft = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <polyline points="15 18 9 12 15 6"/>
    </svg>
);
const IcoRight = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
        <polyline points="9 18 15 12 9 6"/>
    </svg>
);

// ── Chip tâche (draggable) ────────────────────────────────────────────────────

const TacheChip = ({ tache, onClick, draggable = false, onDragStart }) => (
    <div
        className={styles.chip}
        style={{ borderLeftColor: tache.statut?.couleur || '#00B0FF' }}
        onClick={e => { e.stopPropagation(); onClick(tache); }}
        title={tache.titre}
        draggable={draggable}
        onDragStart={draggable ? (e) => {
            e.stopPropagation();
            onDragStart && onDragStart(e, tache);
        } : undefined}
    >
        <span className={styles.chipTitre}>{tache.titre}</span>
        {tache.responsable && (
            <span className={styles.chipAvatar}>
                {tache.responsable.prenom?.[0]}{tache.responsable.nom?.[0]}
            </span>
        )}
    </div>
);

// ── Vue Jour — maille journée pure ───────────────────────────────────────────

const VueJour = ({ date, joursData, onCellClick, onTacheClick }) => {
    const key = toYMD(date);
    const taches = joursData[key] || [];
    const jourLabel = date.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
    const isoWeek = getISOWeek(date);

    return (
        <div className={styles.vueJour} onDoubleClick={() => onCellClick(date, null)}>
            <div className={styles.jourDayHeader}>
                <span className={`${styles.jourDayNum} ${isToday(date) ? styles.jourDayNumToday : ''}`}>
                    {date.getDate()}
                </span>
                <div className={styles.jourDayMeta}>
                    <span className={styles.jourDayLabel}>{jourLabel}</span>
                    <span className={styles.isoWeekBadge} title="Numéro de semaine ISO">S{isoWeek}</span>
                </div>
                <span className={styles.jourHint}>Double-clic pour ajouter</span>
            </div>
            <div className={styles.jourTachesList}>
                {taches.length === 0 ? (
                    <div className={styles.jourEmpty}>Aucune action ce jour — double-cliquez pour en créer une</div>
                ) : (
                    taches.map(t => (
                        <div key={t.id} className={styles.jourTacheRow}>
                            <TacheChip tache={t} onClick={onTacheClick} />
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

// ── Vue Semaine — maille journée pure ────────────────────────────────────────

const VueSemaine = ({ date, joursData, onCellClick, onTacheClick, onDropTache, onDropFromReception }) => {
    const lundi = startOfWeek(date);
    const jours = Array.from({ length: 7 }, (_, i) => addDays(lundi, i));
    const isoWeek = getISOWeek(lundi);

    const [dragOverKey, setDragOverKey] = useState(null);
    const dragTacheRef = useRef(null);

    const handleDragStart = (e, tache) => {
        dragTacheRef.current = tache;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(tache.id));
    };
    const handleDragOver = (e, key) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverKey(key);
    };
    const handleDragLeave = () => setDragOverKey(null);
    const handleDrop = (e, j) => {
        e.preventDefault();
        setDragOverKey(null);
        // Tâche interne (drag depuis le calendrier)
        const tache = dragTacheRef.current;
        if (tache) {
            onDropTache(tache, toYMD(j));
            dragTacheRef.current = null;
            return;
        }
        // Tâche depuis boîte de réception
        const tacheIdStr = e.dataTransfer.getData('application/reception-tache');
        if (tacheIdStr && onDropFromReception) {
            onDropFromReception(tacheIdStr, toYMD(j));
        }
    };

    return (
        <div className={styles.vueSemaine}>
            {/* Header jours — aligné sur les colonnes du corps */}
            <div className={styles.semaineHeader}>
                <span className={styles.isoWeekBadge} title="Numéro de semaine ISO">S{isoWeek}</span>
                {jours.map((j, i) => (
                    <div key={i} className={`${styles.jourHeader} ${isToday(j) ? styles.jourHeaderToday : ''}`}>
                        <span className={styles.jourNom}>{JOURS_COURT[i]}</span>
                        <span className={`${styles.jourNum} ${isToday(j) ? styles.jourNumToday : ''}`}>
                            {j.getDate()}
                        </span>
                    </div>
                ))}
            </div>

            {/* Colonnes journée — FIX: spacer 40px aligné avec isoWeekBadge du header */}
            <div className={styles.semaineCorps}>
                {/* Spacer vide pour aligner avec le badge S{isoWeek} du header */}
                <div className={styles.semaineSpacerCorps} />
                {jours.map((j, i) => {
                    const key = toYMD(j);
                    const taches = joursData[key] || [];
                    const isDragOver = dragOverKey === key;
                    return (
                        <div
                            key={i}
                            className={[
                                styles.semaineColonne,
                                isToday(j) ? styles.semaineColonneToday : '',
                                isDragOver ? styles.semaineColonneDragOver : '',
                            ].join(' ')}
                            onDoubleClick={() => onCellClick(j, null)}
                            onDragOver={e => handleDragOver(e, key)}
                            onDragLeave={handleDragLeave}
                            onDrop={e => handleDrop(e, j)}
                        >
                            {taches.length === 0 ? (
                                <div className={styles.semaineEmpty} />
                            ) : (
                                taches.map(t => (
                                    <div
                                        key={t.id}
                                        draggable
                                        onDragStart={e => handleDragStart(e, t)}
                                        style={{ cursor: 'grab' }}
                                    >
                                        <TacheChip tache={t} onClick={onTacheClick} />
                                    </div>
                                ))
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Vue Mois avec Drag & Drop + numéros de semaine ISO ───────────────────────

const VueMois = ({ date, joursData, onCellClick, onTacheClick, onDropTache, onDropFromReception }) => {
    const year  = date.getFullYear();
    const month = date.getMonth();
    const firstDay    = new Date(year, month, 1);
    const startOffset = (firstDay.getDay() + 6) % 7;
    const lastDay     = new Date(year, month + 1, 0).getDate();
    const totalCells  = Math.ceil((startOffset + lastDay) / 7) * 7;
    const cells       = Array.from({ length: totalCells }, (_, i) => new Date(year, month, 1 - startOffset + i));
    const nbWeeks     = totalCells / 7;
    const MAX = 3;

    const [dragOverKey, setDragOverKey] = useState(null);
    const dragTacheRef = useRef(null);

    const handleDragStart = (e, tache) => {
        dragTacheRef.current = tache;
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(tache.id));
    };

    const handleDragOver = (e, key) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDragOverKey(key);
    };

    const handleDragLeave = () => setDragOverKey(null);

    const handleDrop = (e, d) => {
        e.preventDefault();
        setDragOverKey(null);
        // Tâche interne (drag depuis le calendrier)
        const tache = dragTacheRef.current;
        if (tache) {
            onDropTache(tache, toYMD(d));
            dragTacheRef.current = null;
            return;
        }
        // Tâche depuis boîte de réception
        const tacheIdStr = e.dataTransfer.getData('application/reception-tache');
        if (tacheIdStr && onDropFromReception) {
            onDropFromReception(tacheIdStr, toYMD(d));
        }
    };

    return (
        <div className={styles.vueMois}>
            {/* Header jours — FIX: spacer 30px pour les numéros de semaine */}
            <div className={styles.moisHeader}>
                <div className={styles.moisWeekNumHeader} />
                {JOURS_COURT.map(j => <div key={j} className={styles.moisJourLabel}>{j}</div>)}
            </div>
            {/* Grille semaines avec numéros ISO */}
            <div className={styles.moisGridWrapper}>
                {Array.from({ length: nbWeeks }, (_, w) => {
                    const lundiSemaine = cells[w * 7];
                    const isoWeek = getISOWeek(lundiSemaine);
                    return (
                        <div key={w} className={styles.moisLigne}>
                            {/* Numéro de semaine */}
                            <div className={styles.moisWeekNum} title={`Semaine ${isoWeek}`}>
                                S{isoWeek}
                            </div>
                            {/* 7 cellules de la semaine */}
                            {cells.slice(w * 7, w * 7 + 7).map((d, i) => {
                                const key = toYMD(d);
                                const taches = joursData[key] || [];
                                const inMonth = d.getMonth() === month;
                                const isDragOver = dragOverKey === key;
                                return (
                                    <div
                                        key={i}
                                        className={[
                                            styles.moisCell,
                                            !inMonth ? styles.moisCellOut : '',
                                            isToday(d) ? styles.moisCellToday : '',
                                            isDragOver ? styles.moisCellDragOver : '',
                                        ].join(' ')}
                                        onDoubleClick={() => onCellClick(d, null)}
                                        onDragOver={e => handleDragOver(e, key)}
                                        onDragLeave={handleDragLeave}
                                        onDrop={e => handleDrop(e, d)}
                                    >
                                        <div className={styles.moisCellNum}>{d.getDate()}</div>
                                        <div className={styles.moisCellTaches}>
                                            {taches.slice(0, MAX).map(t => (
                                                <TacheChip
                                                    key={t.id}
                                                    tache={t}
                                                    onClick={onTacheClick}
                                                    draggable
                                                    onDragStart={handleDragStart}
                                                />
                                            ))}
                                            {taches.length > MAX && <div className={styles.moisMore}>+{taches.length - MAX}</div>}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// ── Composant principal ───────────────────────────────────────────────────────

const LS_KEY_MODE = 'seizo_cal_view_mode';
const LS_KEY_DATE = 'seizo_cal_current_date';

const VueCalendaire = observer(() => {
    const { PlanningActionsStore } = useStores();
    const { structure } = PlanningActionsStore;

    // Persistance : restaurer mode et date depuis localStorage
    const initMode = () => {
        const stored = localStorage.getItem(LS_KEY_MODE);
        return stored || PlanningActionsStore.calViewMode || 'mois';
    };
    const initDate = () => {
        const stored = localStorage.getItem(LS_KEY_DATE);
        if (stored) {
            const d = new Date(stored);
            if (!isNaN(d.getTime())) return d;
        }
        return new Date();
    };

    const [mode, setMode]           = useState(initMode);
    const [current, setCurrent]     = useState(initDate);
    const [joursData, setJoursData] = useState({});
    const [loading, setLoading]     = useState(true);

    // ── Chargement ────────────────────────────────────────────────────────
    // calendarVersion est observé depuis le store : incrémenté après chaque saveTask
    const calendarVersion = PlanningActionsStore.calendarVersion;

    const loadTaches = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API_URL}/api/PA/taches-calendrier/${PlanningActionsStore.idPlanning}`,
                {
                    params: { vue: mode, date: toYMD(current) },
                    headers: { Authorization: `Bearer ${PlanningActionsStore._token}` },
                }
            );

            // Normaliser les clés de date pour éviter les décalages UTC
            const raw = res.data.jours || {};
            const normalized = {};
            Object.entries(raw).forEach(([k, v]) => {
                const normKey = normalizeDate(k) || k;
                normalized[normKey] = (normalized[normKey] || []).concat(v);
            });
            setJoursData(normalized);
        } catch (e) { console.error('VueCalendaire:', e); }
        finally { setLoading(false); }
    }, [mode, current, PlanningActionsStore.idPlanning, PlanningActionsStore._token, calendarVersion]);

    useEffect(() => { loadTaches(); }, [loadTaches]);

    // Persister mode dans store + localStorage
    useEffect(() => {
        PlanningActionsStore.calViewMode = mode;
        localStorage.setItem(LS_KEY_MODE, mode);
    }, [mode]);

    // Persister date courante dans localStorage
    useEffect(() => {
        localStorage.setItem(LS_KEY_DATE, toYMD(current));
    }, [current]);

    // ── Navigation ────────────────────────────────────────────────────────
    const navigate = (dir) => {
        setCurrent(prev => {
            const d = new Date(prev);
            if (mode === 'jour')    d.setDate(d.getDate() + dir);
            if (mode === 'semaine') d.setDate(d.getDate() + dir * 7);
            if (mode === 'mois')    d.setMonth(d.getMonth() + dir);
            return d;
        });
    };

    // ── Handlers ──────────────────────────────────────────────────────────
    const handleCellClick = (date, heure) => {
        // Pré-remplir l'échéance avec la date cliquée dans le calendrier
        PlanningActionsStore.calNewTacheDate = toYMD(date);
        if (heure != null) PlanningActionsStore.calNewTacheHeure = `${String(heure).padStart(2,'0')}:00`;
        // Ouvrir le tiroir en mode création
        PlanningActionsStore.openModalFormAction(null, null, null, null);
    };

    const handleTacheClick = (tache) => {
        PlanningActionsStore.openModalFormAction(tache.colonne_id, tache.sous_colonne_id, tache.ligne_id, tache.id);
    };

    /**
     * Drag & drop interne : déplace la date_estimee de la tâche
     */
    const handleDropTache = async (tache, newDateYMD) => {
        if (normalizeDate(tache.date_estimee) === newDateYMD) return; // pas de changement

        // Mise à jour optimiste locale
        setJoursData(prev => {
            const next = { ...prev };
            // Retirer de l'ancienne date
            const oldKey = normalizeDate(tache.date_estimee);
            if (oldKey && next[oldKey]) {
                next[oldKey] = next[oldKey].filter(t => t.id !== tache.id);
            }
            // Ajouter à la nouvelle date
            const updated = { ...tache, date_estimee: newDateYMD };
            next[newDateYMD] = [...(next[newDateYMD] || []), updated];
            return next;
        });

        // Mise à jour en base via saveTask
        try {
            await PlanningActionsStore.saveTask({
                id:             tache.id,
                titre:          tache.titre,
                description:    tache.description,
                date_estimee:   newDateYMD,
                date_debut:     tache.date_debut || null,
                priorite:       tache.priorite,
                pourcentage:    tache.pourcentage,
                responsable_id: tache.responsable?.id || null,
                statut_id:      tache.statut?.id || null,
                colonne_id:     tache.colonne_id,
                sous_colonne_id:tache.sous_colonne_id || null,
                ligne_id:       tache.ligne_id,
            });
        } catch (e) {
            console.error('Erreur drag & drop date:', e);
            // Rollback : recharger
            loadTaches();
        }
    };

    /**
     * Drag & drop depuis boîte de réception → calendrier
     * Planifie la tâche à la date cible et la retire de la boîte de réception
     */
    const handleDropFromReception = async (tacheId, newDateYMD) => {
        try {
            await PlanningActionsStore.accepterTacheReceptionVersCalendrier(tacheId, newDateYMD);
            // Recharger le calendrier pour afficher la tâche planifiée
            loadTaches();
        } catch (e) {
            console.error('Erreur drag & drop depuis réception:', e);
        }
    };

    if (!structure) {
        return (
            <div className={styles.loadingState}>
                <div className={styles.loadingSpinner} />
                <span>Chargement…</span>
            </div>
        );
    }

    return (
        <div className={styles.calRoot}>

            {/* ── Barre navigation ────────────────────────────────────── */}
            <div className={styles.navBar}>
                <div className={styles.modeSelector}>
                    {['jour', 'semaine', 'mois'].map(m => (
                        <button
                            key={m}
                            className={`${styles.modeBtn} ${mode === m ? styles.modeBtnActive : ''}`}
                            onClick={() => setMode(m)}
                        >
                            {m.charAt(0).toUpperCase() + m.slice(1)}
                        </button>
                    ))}
                </div>
                <div className={styles.navControls}>
                    <button className={styles.navBtn} onClick={() => navigate(-1)}><IcoLeft /></button>
                    <button className={styles.todayBtn} onClick={() => setCurrent(new Date())}>Aujourd'hui</button>
                    <button className={styles.navBtn} onClick={() => navigate(1)}><IcoRight /></button>
                </div>
                <div className={styles.periodLabel}>{getPeriodeLabel(mode, current)}</div>
            </div>

            {/* ── Corps ───────────────────────────────────────────────── */}
            <div className={styles.body}>
                {loading ? (
                    <div className={styles.loadingInline}>
                        <div className={styles.loadingSpinner} />
                    </div>
                ) : (
                    <>
                        {mode === 'jour' && (
                            <VueJour date={current} joursData={joursData} onCellClick={handleCellClick} onTacheClick={handleTacheClick} />
                        )}
                        {mode === 'semaine' && (
                            <VueSemaine
                                date={current}
                                joursData={joursData}
                                onCellClick={handleCellClick}
                                onTacheClick={handleTacheClick}
                                onDropTache={handleDropTache}
                                onDropFromReception={handleDropFromReception}
                            />
                        )}
                        {mode === 'mois' && (
                            <VueMois
                                date={current}
                                joursData={joursData}
                                onCellClick={handleCellClick}
                                onTacheClick={handleTacheClick}
                                onDropTache={handleDropTache}
                                onDropFromReception={handleDropFromReception}
                            />
                        )}
                    </>
                )}
            </div>


        </div>
    );
});

export default VueCalendaire;
